import React from 'react';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import './AttendancePhysicalVerificationSheet.css';

export type AttendanceVerificationRow = {
  employee_code: string;
  employee_name: string;
  check_in: string;
  source: string;
  missing_checkout: boolean;
  status: string;
};

export type AttendancePhysicalVerificationDoc = {
  company_name: string;
  attendance_date: string;
  printed_at: string;
  printed_by: string;
  rows: AttendanceVerificationRow[];
  summary: {
    checked_in: number;
    missing_checkout: number;
    completed_out: number;
    /** Used by missing_checkin variant */
    missing_checkin?: number;
    active_employees?: number;
  };
};

export type AttendancePrintVariant = 'physical_verification' | 'missing_checkin';

function VerificationPage({
  doc,
  variant,
}: {
  doc: AttendancePhysicalVerificationDoc;
  variant: AttendancePrintVariant;
}) {
  const isMissing = variant === 'missing_checkin';

  return (
    <div className="print-doc-a4 apv-page">
      <div className="apv-sheet">
        <header className="apv-head">
          <div className="apv-company">{doc.company_name || 'Company'}</div>
          <div className="apv-title">
            {isMissing ? 'Missing check-in register' : 'Attendance physical verification'}
          </div>
          <div className="apv-meta">
            <span>
              Date: <strong>{doc.attendance_date}</strong>
            </span>
            <span>
              Printed: <strong>{doc.printed_at}</strong>
            </span>
            <span>
              By: <strong>{doc.printed_by || '—'}</strong>
            </span>
          </div>
        </header>

        <div className="apv-summary">
          {isMissing ? (
            <>
              <div>
                <strong>{doc.summary.active_employees ?? '—'}</strong>
                <span>Active employees</span>
              </div>
              <div>
                <strong>{doc.summary.checked_in}</strong>
                <span>Checked in</span>
              </div>
              <div>
                <strong className={doc.summary.missing_checkin ? 'apv-warn-num' : undefined}>
                  {doc.summary.missing_checkin ?? doc.rows.length}
                </strong>
                <span>Missing check-in</span>
              </div>
              <div>
                <strong>{doc.rows.length}</strong>
                <span>On this sheet</span>
              </div>
            </>
          ) : (
            <>
              <div>
                <strong>{doc.summary.checked_in}</strong>
                <span>Checked in</span>
              </div>
              <div>
                <strong className={doc.summary.missing_checkout ? 'apv-warn-num' : undefined}>
                  {doc.summary.missing_checkout}
                </strong>
                <span>Missing checkout</span>
              </div>
              <div>
                <strong>{doc.summary.completed_out}</strong>
                <span>Completed out</span>
              </div>
              <div>
                <strong>{doc.rows.length}</strong>
                <span>On this sheet</span>
              </div>
            </>
          )}
        </div>

        <p className="apv-note">
          {isMissing ? (
            <>
              Contact or walk the site for each person below. Tick <strong>Located</strong> if found (then record
              late check-in or reason). Tick <strong>Absent</strong> if not present — then upsert an Absent (or Leave)
              day in ORDAN. Leave / holiday / weekly-off employees are excluded from this list.
            </>
          ) : (
            <>
              Walk the floor and tick <strong>Seen</strong> only when the person is physically present. Use Notes for
              exceptions (left site, wrong punch, proxy, etc.). Highlighted rows have check-in but no check-out yet.
            </>
          )}
        </p>

        <table className="apv-table">
          <thead>
            {isMissing ? (
              <tr>
                <th className="apv-num">#</th>
                <th className="apv-code">Emp. code</th>
                <th>Employee</th>
                <th className="apv-src">Status</th>
                <th className="apv-check">Located</th>
                <th className="apv-check">Absent</th>
                <th>Notes / reason</th>
              </tr>
            ) : (
              <tr>
                <th className="apv-num">#</th>
                <th className="apv-code">Emp. code</th>
                <th>Employee</th>
                <th className="apv-in">In</th>
                <th className="apv-src">Source</th>
                <th className="apv-flag">Checkout</th>
                <th className="apv-check">Seen</th>
                <th>Notes</th>
              </tr>
            )}
          </thead>
          <tbody>
            {doc.rows.map((r, i) =>
              isMissing ? (
                <tr key={`${r.employee_code}-${i}`}>
                  <td className="apv-num">{i + 1}</td>
                  <td className="apv-code">{r.employee_code}</td>
                  <td>{r.employee_name}</td>
                  <td className="apv-src">{r.status === 'NoRecord' ? 'No record' : r.status || '—'}</td>
                  <td className="apv-check">
                    <span className="apv-box" />
                  </td>
                  <td className="apv-check">
                    <span className="apv-box" />
                  </td>
                  <td />
                </tr>
              ) : (
                <tr key={`${r.employee_code}-${i}`}>
                  <td className="apv-num">{i + 1}</td>
                  <td className="apv-code">{r.employee_code}</td>
                  <td>{r.employee_name}</td>
                  <td className="apv-in">{r.check_in || '—'}</td>
                  <td className="apv-src">{r.source || '—'}</td>
                  <td className="apv-flag">{r.missing_checkout ? 'Missing' : 'OK'}</td>
                  <td className="apv-check">
                    <span className="apv-box" />
                  </td>
                  <td />
                </tr>
              )
            )}
            {!doc.rows.length && (
              <tr>
                <td colSpan={isMissing ? 7 : 8} style={{ textAlign: 'center', color: '#6b7280' }}>
                  {isMissing
                    ? 'No missing check-ins for this date.'
                    : 'No checked-in employees for this date.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="apv-signs">
          <div className="apv-sign">
            <div className="apv-sign-label">Verified by</div>
            <div className="apv-sign-hint">Name / signature / time</div>
          </div>
          <div className="apv-sign">
            <div className="apv-sign-label">Supervisor</div>
            <div className="apv-sign-hint">Name / signature</div>
          </div>
          <div className="apv-sign">
            <div className="apv-sign-label">HR acknowledgement</div>
            <div className="apv-sign-hint">Name / signature / date</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const AttendancePhysicalVerificationSheet: React.FC<{
  doc: AttendancePhysicalVerificationDoc;
  onClose: () => void;
  variant?: AttendancePrintVariant;
}> = ({ doc, onClose, variant = 'physical_verification' }) => {
  const isMissing = variant === 'missing_checkin';
  const fileName = isMissing
    ? `attendance-missing-checkin-${doc.attendance_date}`
    : `attendance-physical-verification-${doc.attendance_date}`;
  return (
    <PrintPreviewChrome
      title={
        isMissing
          ? `Missing check-in — ${doc.attendance_date}`
          : `Physical verification — ${doc.attendance_date}`
      }
      fileName={fileName}
      onBack={onClose}
    >
      <VerificationPage doc={doc} variant={variant} />
    </PrintPreviewChrome>
  );
};

export default AttendancePhysicalVerificationSheet;
